import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { fallAnalysisValidator } from "../formUtils";
import { useAnalyticsWrapper, useStoreWrapper } from "../../../../../store/store";
import moment from "moment";
import dayjs from "dayjs";
import * as yup from "yup";

export const useFormManagement = ({ submitFn }: any) => {
  const [facilityOptions, setFacilityOptions] = useState<any>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const { setStartDate, setEndDate, setFallFacility } = useAnalyticsWrapper(
    (state) => ({
      setStartDate: state.setStartDate,
      setEndDate: state.setEndDate,
      setFallFacility: state.setFallFacility,
    })
  );

  const { userFacilities } = useStoreWrapper((state) => ({
    userFacilities: state.userFacilities,
  }));

  const formik = useFormik({
    initialValues: {
      facilityInfo: "",
      startDate: "",
      endDate: "",
    },
    validationSchema: fallAnalysisValidator,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      if (formik.isValid) {
        setIsFetching(true);
        setTimeout(() => {
          setIsFetching(false);
          setFallFacility(values.facilityInfo);
          setStartDate(values.startDate);
          setEndDate(values.endDate);
          submitFn();
        }, 1000);
      }
    },
  });

  const isSubmitDisabled =
    moment(formik.values.startDate).isAfter(moment(formik.values.endDate)) ||
    isFetching ||
    !formik.isValid;

  useEffect(() => {
    if (userFacilities?.length) {
      let options = userFacilities.map((facility: any) => {
        const { facilityName, facilityId } = facility;
        return { label: facilityName, value: facilityId };
      });
      options = options.sort((a: any, b: any) => (a.label > b.label ? 1 : -1));
      setFacilityOptions(options);
    }
  }, [userFacilities]);

  useEffect(() => {
    formik.validateForm();
  }, [formik.values]);

  return {
    facilityOptions,
    isFetching,
    setFieldValue: formik.setFieldValue,
    handleSubmit: formik.handleSubmit,
    values: formik.values,
    isValid: formik.isValid,
    isSubmitDisabled,
  };
};

export const combineDateAndTime = (data: any) => {
  const { startDate, startTime } = data;

  const date = dayjs(startDate);
  const time = dayjs(startTime);

  return dayjs(date)
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second())
    .format();
};

export const extractTime = (dateTimeString: string) => {
  // Parse the date-time string using dayjs
  const dateTime = dayjs(dateTimeString);

  // Format the date part as "YYYY-MM-DD"
  const datePart = dateTime.format("YYYY-MM-DD");

  // Convert the time to UTC and format it as "HH:mm:ss"
  const timePartInUTC = dateTime.utc().format("HH:mm:ss");

  // Combine the date and the UTC time
  return `${datePart}T${timePartInUTC}Z`;
};

export const emailSubscriptionValidationSchema = (isFacility: boolean) => {
  return yup.object({
    description: yup.string().required("Description is required"),
    recurrence: yup.string().required("Recurrence is required"),
    startDate: yup
      .date()
      .required("Start date is required")
      .typeError("Invalid date"),
    endDate: yup.date().nullable(),
    facility: yup.string().nullable(),
    weeklyDays: yup
      .string()
      .nullable()
      .when("recurrence", {
        is: "weekly",
        then: yup
          .string()
          .required("Weekly days are required")
          .typeError("Weekly days are required"),
      }),
    monthlyDay: yup
      .number()
      .nullable()
      .when("recurrence", {
        is: "monthly",
        then: yup
          .number()
          .required("Monthly day is required")
          .typeError("Monthly day is required"),
      }),
    periodType: yup
      .string()
      .required("Period is required")
      .typeError("Period is required"),
    periodLength: yup
      .string()
      .required("Length is required")
      .typeError("Length is required"),
  });
};

export const getInitialValues = (
  selectedSubscription: any,
  dateFormat: string
) => {
  return {
    description: selectedSubscription?.Description__c || "",
    recurrence: selectedSubscription?.Interval_Type__c.toLowerCase() || "daily",
    startDate: selectedSubscription?.Start_Date__c
      ? dayjs(selectedSubscription?.Start_Date__c, dateFormat)
      : null,
    endDate: selectedSubscription?.End_Date__c
      ? dayjs(selectedSubscription?.End_Date__c, dateFormat)
      : null,
    facility: selectedSubscription?.Facility__c || null,
    weeklyDays: selectedSubscription?.Day_Of_The_Week__c || null,
    monthlyDay: selectedSubscription?.Day_Of_The_Month__c || null,
    periodType: selectedSubscription?.Trailing_Period__c || "",
    periodLength: selectedSubscription?.Trailing_Period_Quantity__c || "",
  };
};
